import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import CommonService from "../../service/CommonService";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  SwipeableDrawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Business,
  CheckCircle,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Cancel,
  LocationOn,
  Visibility,
  AccountTree,
  ArrowBack,
  Person,
  Email,
  Phone,
  Work,
  CalendarToday,
  AttachMoney,
  ReceiptLong,
  AccessTime,
  InfoOutlined,
  EventNote,
  Search,
} from "@mui/icons-material";
import axios from "axios";

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const isPresent = (emp) => emp.inn && emp.inn !== "NR" && emp.inn !== "";

const normalizeRow = (item) => ({
  division: (item?.Division || item?.division || "").trim() || "Unknown",
  divName: (item?.DivName || item?.divName || "").trim(),
  loc: (item?.Location || item?.location || "").trim() || "Unknown",
  locName: (item?.LocName || item?.locName || "").trim(),
  sno: item?.Sno || item?.sno || "",
  repname: item?.Name || item?.name || "",
  des: item?.Desc || item?.des || "",
  inn: item?.CIN || item?.inn || "",
  pout: item?.COUT || item?.pout || "",
  cno: item?.CNO || item?.cno || "",
});

/* ─── Bar-list appearance helpers (ported from the reference/first component) ─── */
const rateColor = (pct) =>
  pct >= 80 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";

const rateBg = (pct) =>
  pct >= 80 ? "#dcfce7" : pct >= 60 ? "#fef3c7" : "#fee2e2";

function PctBadge({ pct }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: "7px",
        py: "1px",
        borderRadius: "10px",
        fontSize: 11,
        fontWeight: 600,
        background: rateBg(pct),
        color: rateColor(pct),
        flexShrink: 0,
      }}
    >
      {pct}%
    </Box>
  );
}

const RateLegend = () => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      justifyContent: "center",
      pt: "10px",
      mt: "8px",
      borderTop: "0.5px solid #f1f5f9",
    }}
  >
    {[
      { label: "≥80%", color: "#16a34a" },
      { label: "60–79%", color: "#d97706" },
      { label: "<60%", color: "#dc2626" },
    ].map((l) => (
      <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "2px", background: l.color }} />
        <Typography sx={{ fontSize: "0.62rem", color: "#64748b" }}>{l.label}</Typography>
      </Box>
    ))}
  </Box>
);

/* ─── InfoCard ────────────────────────────────────────────────────────────── */
const iconBoxColors = {
  blue: { bg: "#e6f1fb", color: "#185FA5" },
  purple: { bg: "#EEEDFE", color: "#534AB7" },
  red: { bg: "#FCEBEB", color: "#D85A30" },
  teal: { bg: "#E1F5EE", color: "#0F6E56" },
  amber: { bg: "#FAEEDA", color: "#854F0B" },
  green: { bg: "#EAF3DE", color: "#3B6D11" },
};

const InfoCard = ({ icon, label, value, colorKey = "blue", action }) => {
  const c = iconBoxColors[colorKey] || iconBoxColors.blue;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.4,
        borderBottom: "0.5px solid #e2e8f0",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: c.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "& svg": { fontSize: 19, color: c.color },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            mb: "1px",
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.88rem",
            color: "#1e293b",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || "-"}
        </Typography>
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
  );
};

/* ─── PlaceholderTab ──────────────────────────────────────────────────────── */
const PlaceholderTab = ({ icon, message }) => (
  <Box sx={{ textAlign: "center", py: 7 }}>
    <Box sx={{ fontSize: 44, color: "#cbd5e1", mb: 1 }}>{icon}</Box>
    <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>{message}</Typography>
  </Box>
);

/* ─── AttendanceTab ───────────────────────────────────────────────────────── */
const AttendanceTab = ({ sno }) => {
  const [attData, setAttData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sno) return;
    const fetchAtt = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await CommonService.GetEmployeeAttSummary(sno);
        if (response?.data?.StatusCode === 200 && Array.isArray(response.data.ResultSet)) {
          const sorted = [...response.data.ResultSet].sort((a, b) => Number(b.year) - Number(a.year));
          setAttData(sorted);
        } else {
          setAttData([]);
        }
      } catch (e) {
        console.error("Attendance fetch error:", e);
        setError("Failed to load attendance data.");
      } finally {
        setLoading(false);
      }
    };
    fetchAtt();
  }, [sno]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress sx={{ color: "#004AAD" }} size={32} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <AccessTime sx={{ fontSize: 40, color: "#fca5a5", mb: 1 }} />
        <Typography sx={{ color: "#dc2626", fontSize: "0.82rem" }}>{error}</Typography>
      </Box>
    );
  }
  if (!attData.length) {
    return <PlaceholderTab icon={<AccessTime sx={{ fontSize: 44 }} />} message="No attendance records found" />;
  }

  const columns = [
    { label: "#", align: "left" },
    { label: "YEAR", align: "left" },
    { label: "PERCENTAGE", align: "right" },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ border: "0.5px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
        {/* Table Header */}
        <Box sx={{ display: "grid", gridTemplateColumns: "40px 1fr auto", px: 2, py: 1.2, bgcolor: "#f1f5f9" }}>
          {columns.map(({ label, align }) => (
            <Typography key={label} sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.07em", textAlign: align }}>
              {label}
            </Typography>
          ))}
        </Box>

        {/* Data Rows */}
        {attData.map((item, idx) => {
          const pct = parseFloat(item.pres_Percentage) || 0;
          const pctText = `${pct.toFixed(0)}%`;
          const txtColor = pct >= 75 ? "#0d28c5" : pct >= 50 ? "#d9c406" : "#dc2626";
          return (
            <Box
              key={idx}
              sx={{
                display: "grid",
                gridTemplateColumns: "40px 1fr auto",
                px: 2,
                py: 1.4,
                borderTop: idx > 0 ? "0.5px solid #e2e8f0" : "none",
                alignItems: "center",
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#f8faff" },
              }}
            >
              <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 600 }}>{idx + 1}</Typography>
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#1e293b" }}>{item.year}</Typography>
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: txtColor, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {pctText}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const AttendanceCardTab = ({ sno }) => {
  const [attendanceCardData, setAttendanceCardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedReason, setSelectedReason] = useState(null);
  const [mapModal, setMapModal] = useState({ open: false, item: null, activeType: "in" });

  const getLocationData = (item, type) => {
    if (!item) return { lat: null, lng: null, time: "—", clockNo: "—" };
    if (type === "in") {
      const lat =
        item.In_Latitude ??
        item.In_Lat ??
        item.IN_LATITUDE ??
        item.IN_LAT ??
        item.InLatitude ??
        item.InLat ??
        item.Latitude ??
        item.lat ??
        null;
      const lng =
        item.In_Longitude ??
        item.In_Lng ??
        item.In_Lon ??
        item.IN_LONGITUDE ??
        item.IN_LNG ??
        item.InLongitude ??
        item.InLng ??
        item.Longitude ??
        item.lng ??
        null;
      return {
        lat: lat !== null && lat !== undefined ? String(lat).trim() : null,
        lng: lng !== null && lng !== undefined ? String(lng).trim() : null,
        time: item.InTime || "—",
        clockNo: item.In_Clock_no || "—",
      };
    } else {
      const lat =
        item.Out_Latitude ??
        item.Out_Lat ??
        item.OUT_LATITUDE ??
        item.OUT_LAT ??
        item.OutLatitude ??
        item.OutLat ??
        item.Latitude ??
        item.lat ??
        null;
      const lng =
        item.Out_Longitude ??
        item.Out_Lng ??
        item.Out_Lon ??
        item.OUT_LONGITUDE ??
        item.OUT_LNG ??
        item.OutLongitude ??
        item.OutLng ??
        item.Longitude ??
        item.lng ??
        null;
      return {
        lat: lat !== null && lat !== undefined ? String(lat).trim() : null,
        lng: lng !== null && lng !== undefined ? String(lng).trim() : null,
        time: item.OutTime || "—",
        clockNo: item.Out_Clock_no || "—",
      };
    }
  };

  const getAuthKey = () => {
    const token = localStorage.getItem("token");
    if (token) {
      return token.replace(/^"|"$/g, "");
    }
    return null;
  };

  const getDayType = (date) => {
    if (!date) return "weekday";
    const d = new Date(date);
    const dayOfWeek = d.getDay();
    if (dayOfWeek === 6) return "saturday";
    if (dayOfWeek === 0) return "sunday";
    return "weekday";
  };

  const getBackgroundColor = (date) => {
    const dayType = getDayType(date);
    if (dayType === "saturday") return "#E6E6FA";
    if (dayType === "sunday") return "#B0C4DE";
    return "transparent";
  };

  const getHoverColor = (date, hasInTime) => {
    const dayType = getDayType(date);
    if (dayType === "saturday") return "#E0E0FA";
    if (dayType === "sunday") return "#D0B8D0";
    return hasInTime ? "#f8faff" : "#fee2e2";
  };

  const getClockColor = (clockNo) => {
    if (!clockNo) return "#000";
    const num = parseInt(clockNo);
    if (num === 33) return "#FF6B6B";
    if (num === 31 || num === 32) return "#6947ff";
    return "#000";
  };

  const isSpecialClock = (clockNo) => {
    if (!clockNo) return false;
    const num = parseInt(clockNo);
    return num === 31 || num === 32 || num === 33;
  };

  const getAllDatesInMonth = (yearMonth) => {
    const [year, month] = yearMonth.split("-").map(Number);
    const date = new Date(year, month - 1, 1);
    const dates = [];
    while (date.getMonth() === month - 1) {
      dates.push({
        Date: new Date(date),
        Day: date.toLocaleDateString("en-US", { weekday: "short" }),
        InTime: null,
        OutTime: null,
        dateKey: new Date(date).toISOString().split("T")[0],
        dayNumber: date.getDate(),
      });
      date.setDate(date.getDate() + 1);
    }
    return dates;
  };

  const mergeWithFullMonth = (apiData, yearMonth) => {
    const allDates = getAllDatesInMonth(yearMonth);
    const apiDataMap = new Map();
    apiData.forEach((item) => {
      if (item.Date) {
        const dateKey = new Date(item.Date).toISOString().split("T")[0];
        apiDataMap.set(dateKey, item);
      }
    });

    return allDates.map((dateObj) => {
      const apiItem = apiDataMap.get(dateObj.dateKey);
      if (apiItem) {
        return {
          ...apiItem,
          Date: dateObj.Date,
          Day: dateObj.Day,
          dayNumber: dateObj.dayNumber,
          InTime: apiItem.InTime,
          OutTime: apiItem.OutTime,
          Reason: apiItem.Reason,
        };
      }
      return { ...dateObj, InTime: null, OutTime: null, status: "Absent" };
    });
  };

  const fetchAttendanceCard = async () => {
    if (!sno) return;
    const authKey = getAuthKey();
    if (!authKey) {
      setError("Authentication key not found");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${axios.defaults.baseURL}Attendance/DGESGetAllEmpAttendanceCard?p_month=${selectedMonth}&p_sno=${sno}`,
        { headers: { "auth-key": authKey, "Content-Type": "application/json" } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result?.StatusCode === 200 && Array.isArray(result.ResultSet)) {
        const mergedData = mergeWithFullMonth(result.ResultSet, selectedMonth);
        const sorted = mergedData.sort((a, b) => new Date(a.Date) - new Date(b.Date));
        setAttendanceCardData(sorted);
      } else {
        const emptyMonthData = getAllDatesInMonth(selectedMonth);
        setAttendanceCardData(emptyMonthData);
        setError(result?.ResultSet?.length === 0 ? null : result?.Message || "Failed to load attendance card data");
      }
    } catch (e) {
      console.error("Attendance Card fetch error:", e);
      setError("Network error - Failed to load attendance card data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sno && selectedMonth) fetchAttendanceCard();
  }, [sno, selectedMonth]);

  const handleMonthChange = (event) => setSelectedMonth(event.target.value);

  const getMonthOptions = () => {
    const currentYearNum = new Date().getFullYear();
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYearNum, new Date().getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      months.push({ value: `${year}-${month}`, label: date.toLocaleString("default", { month: "long", year: "numeric" }) });
    }
    return months;
  };

  const formatDayNumber = (date) => {
    if (!date) return "-";
    try {
      const d = new Date(date);
      if (isNaN(d)) return "-";
      const day = d.getDate();
      return day.toString().padStart(2, "0");
    } catch (e) {
      return "-";
    }
  };

  // Calculate monthly stats
  const stats = useMemo(() => {
    let present = 0;
    let weekend = 0;
    let absent = 0;
    attendanceCardData.forEach((item) => {
      const dt = getDayType(item.Date);
      if (dt === "saturday" || dt === "sunday") {
        weekend++;
      } else if (item.InTime) {
        present++;
      } else {
        absent++;
      }
    });
    return { total: attendanceCardData.length, present, absent, weekend };
  }, [attendanceCardData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#004AAD" }} size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, height: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Header & Controls */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, color: "#004AAD", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 0.8 }}>
          <CalendarToday sx={{ fontSize: 16 }} />
          Attendance
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}>Month:</Typography>
          <Box
            component="select"
            value={selectedMonth}
            onChange={handleMonthChange}
            sx={{
              px: 1.5,
              py: 0.6,
              fontSize: "0.75rem",
              fontWeight: 600,
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              backgroundColor: "#fff",
              color: "#004AAD",
              cursor: "pointer",
              outline: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.2s ease",
              "&:focus": { borderColor: "#004AAD", boxShadow: "0 0 0 2px rgba(0,74,173,0.1)" },
              "&:hover": { borderColor: "#004AAD" },
            }}
          >
            {getMonthOptions().map((month) => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Monthly Summary Statistics Chips */}

      {/* Main Table Scroll Container */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-track": { bgcolor: "#f1f5f9", borderRadius: 3 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 3, "&:hover": { bgcolor: "#94a3b8" } },
        }}
      >
        {error ? (
          <Box sx={{ textAlign: "center", py: 5, bgcolor: "#fff", borderRadius: "12px", border: "0.5px solid #e2e8f0" }}>
            <AccessTime sx={{ fontSize: 44, color: "#918e8e", mb: 1 }} />
            <Typography sx={{ color: "#918e8e", fontSize: "0.82rem" }}>{error}</Typography>
          </Box>
        ) : attendanceCardData.length === 0 ? (
          <PlaceholderTab icon={<CalendarToday sx={{ fontSize: 44 }} />} message={`No attendance records found for ${selectedMonth}`} />
        ) : (
          <Box
            sx={{
              border: "1px solid #cbd5e1",
              borderRadius: "12px",
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "60px 1fr 1fr 1.25fr", sm: "75px 1fr 1fr 1.35fr", md: "85px 1fr 1fr 1.4fr" },
                px: { xs: 1, sm: 2 },
                py: 1.1,
                bgcolor: "#f1f5f9",
                position: "sticky",
                top: 0,
                zIndex: 3,
                borderBottom: "1px solid #cbd5e1",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "left", pl: 0.5, whiteSpace: "nowrap" }}>
                DAY
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "center", whiteSpace: "nowrap" }}>
                IN TIME
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "center", whiteSpace: "nowrap" }}>
                OUT TIME
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "center", whiteSpace: "nowrap" }}>
                CLOCK IN/OUT NO
              </Typography>
            </Box>

            {/* Data Rows Container */}
            <Box sx={{ maxHeight: { xs: "calc(88vh - 270px)", sm: "calc(85vh - 250px)", md: "520px" }, overflowY: "auto" }}>
              {attendanceCardData.map((item, index) => {
                const hasInTime = !!item.InTime;
                const bgColor = getBackgroundColor(item.Date);
                const hoverColor = getHoverColor(item.Date, hasInTime);
                const dayType = getDayType(item.Date);
                const inClockColor = getClockColor(item.In_Clock_no);
                const outClockColor = getClockColor(item.Out_Clock_no);
                const isInSpecial = isSpecialClock(item.In_Clock_no);
                const isOutSpecial = isSpecialClock(item.Out_Clock_no);
                const hasReason = !!(item.Reason && item.Reason.trim() !== "");

                return (
                  <Box
                    key={`${item.dateKey || index}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "60px 1fr 1fr 1.25fr", sm: "75px 1fr 1fr 1.35fr", md: "85px 1fr 1fr 1.4fr" },
                      px: { xs: 1, sm: 2 },
                      py: 0.75,
                      borderTop: index > 0 ? "1px solid #e2e8f0" : "none",
                      bgcolor: bgColor,
                      alignItems: "center",
                      transition: "all 0.2s ease",
                      position: "relative",
                      "&:hover": { bgcolor: hoverColor },
                    }}
                  >
                    {/* Column 1: Date & Day - Left aligned with leading zero */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          fontWeight: 700,
                          color: dayType === "saturday" ? "#4a0e4e" : dayType === "sunday" ? "#6b2e6b" : "#475569",
                          minWidth: "20px",
                          lineHeight: 1,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatDayNumber(item.Date)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.62rem", sm: "0.65rem" },
                          fontWeight: 500,
                          color: dayType === "saturday" ? "#4a0e4e" : dayType === "sunday" ? "#6b2e6b" : "#94a3b8",
                          lineHeight: 1,
                        }}
                      >
                        {item.Day}
                      </Typography>
                    </Box>

                    {/* Column 2: In Time */}
                    <Typography
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        color: item.InTime ? "#0f172a" : "#94a3b8",
                        textAlign: "center",
                        fontWeight: item.InTime ? 500 : 400,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.InTime || "—"}
                    </Typography>

                    {/* Column 3: Out Time */}
                    <Typography
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        color: item.OutTime ? "#0f172a" : "#94a3b8",
                        textAlign: "center",
                        fontWeight: item.OutTime ? 500 : 400,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.OutTime || "—"}
                    </Typography>

                    {/* Column 4: Clock Numbers & Reason */}
                    <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 0.3, width: "100%", maxW: "120px" }}>
                        {/* In Clock (right-aligned to slash) */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                          <Typography
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isInSpecial) {
                                setMapModal({ open: true, item, activeType: "in" });
                              }
                            }}
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              color: inClockColor,
                              textAlign: "center",
                              px: isInSpecial ? 0.6 : 0.2,
                              py: 0.15,
                              borderRadius: "4px",
                              bgcolor: isInSpecial ? `${inClockColor}18` : "transparent",
                              border: isInSpecial ? `1.5px solid ${inClockColor}60` : "none",
                              transition: "all 0.2s ease",
                              fontWeight: isInSpecial ? 600 : 400,
                              lineHeight: 1,
                              fontVariantNumeric: "tabular-nums",
                              cursor: isInSpecial ? "pointer" : "default",
                              "&:hover": isInSpecial ? {
                                bgcolor: `${inClockColor}35`,
                                transform: "scale(1.08)"
                              } : {},
                            }}
                            title={isInSpecial ? "Click to view IN TIME location map" : ""}
                          >
                            {item.In_Clock_no || "—"}
                          </Typography>
                        </Box>

                        {/* Center Slash */}
                        <Typography sx={{ fontSize: "0.6rem", color: "#cbd5e1", fontWeight: 300, px: 0.15, textAlign: "center" }}>
                          /
                        </Typography>

                        {/* Out Clock (left-aligned from slash) */}
                        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                          <Typography
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isOutSpecial) {
                                setMapModal({ open: true, item, activeType: "out" });
                              }
                            }}
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              color: outClockColor,
                              textAlign: "center",
                              px: isOutSpecial ? 0.6 : 0.2,
                              py: 0.15,
                              borderRadius: "4px",
                              bgcolor: isOutSpecial ? `${outClockColor}18` : "transparent",
                              border: isOutSpecial ? `1.5px solid ${outClockColor}60` : "none",
                              transition: "all 0.2s ease",
                              fontWeight: isOutSpecial ? 600 : 400,
                              lineHeight: 1,
                              fontVariantNumeric: "tabular-nums",
                              cursor: isOutSpecial ? "pointer" : "default",
                              "&:hover": isOutSpecial ? {
                                bgcolor: `${outClockColor}35`,
                                transform: "scale(1.08)"
                              } : {},
                            }}
                            title={isOutSpecial ? "Click to view OUT TIME location map" : ""}
                          >
                            {item.Out_Clock_no || "—"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Reason Info Icon (absolute right) */}
                      {hasReason && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReason({ date: item.Date, day: item.Day, reason: item.Reason });
                          }}
                          sx={{
                            position: "absolute",
                            right: { xs: 0, sm: 2 },
                            width: 18,
                            height: 18,
                            color: "#004AAD",
                            bgcolor: "#e8f0fe",
                            p: 0,
                            "&:hover": { bgcolor: "#d0e2ff" },
                          }}
                          title="View Manual Attendance Reason"
                        >
                          <InfoOutlined sx={{ fontSize: 13 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Manual Reason Popup Dialog */}
      <Dialog open={!!selectedReason} onClose={() => setSelectedReason(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, color: "#004AAD", fontSize: "0.9rem", display: "flex", alignItems: "center", gap: 0.8 }}>
              <InfoOutlined sx={{ fontSize: 18 }} />
              Manual Attendance Reason
            </Typography>
            <IconButton size="small" onClick={() => setSelectedReason(null)}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mb: 1.2 }}>
            {selectedReason?.day} - {formatDayNumber(selectedReason?.date)}
          </Typography>
          <Box sx={{ bgcolor: "#f8faff", p: 1.5, borderRadius: "10px", border: "0.5px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: "0.85rem", color: "#1e293b", lineHeight: 1.5, wordBreak: "break-word" }}>
              {selectedReason?.reason}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Location Map Popup Dialog */}
      <Dialog
        open={mapModal.open}
        onClose={() => setMapModal({ open: false, item: null, activeType: "in" })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
          },
        }}
      >
        {mapModal.item && (() => {
          const inLoc = getLocationData(mapModal.item, "in");
          const outLoc = getLocationData(mapModal.item, "out");
          const currentLoc = mapModal.activeType === "in" ? inLoc : outLoc;

          const dateStr = mapModal.item.Date
            ? new Date(mapModal.item.Date).toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })
            : "";

          const hasValidCoords =
            currentLoc.lat !== null &&
            currentLoc.lng !== null &&
            currentLoc.lat !== "" &&
            currentLoc.lng !== "" &&
            !isNaN(parseFloat(currentLoc.lat)) &&
            !isNaN(parseFloat(currentLoc.lng)) &&
            parseFloat(currentLoc.lat) !== 0 &&
            parseFloat(currentLoc.lng) !== 0;

          const mapSrc = hasValidCoords
            ? `https://www.google.com/maps?q=${currentLoc.lat},${currentLoc.lng}&z=16&output=embed`
            : null;

          const openExternalMap = () => {
            if (hasValidCoords) {
              window.open(`https://www.google.com/maps?q=${currentLoc.lat},${currentLoc.lng}`, "_blank");
            }
          };

          return (
            <Box>
              {/* Modal Header */}
              <Box
                sx={{
                  bgcolor: "#004AAD",
                  color: "#fff",
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <LocationOn sx={{ fontSize: 24, color: "#fff" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.2 }}>
                      Attendance Location Map
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", opacity: 0.85, mt: 0.3 }}>
                      {dateStr}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setMapModal({ open: false, item: null, activeType: "in" })}
                  sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
                >
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* In / Out Location Toggle Tabs */}
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #e2e8f0",
                  bgcolor: "#f8faff",
                  px: 2,
                  pt: 1,
                  gap: 1,
                }}
              >
                <Button
                  onClick={() => setMapModal((prev) => ({ ...prev, activeType: "in" }))}
                  sx={{
                    flex: 1,
                    py: 1,
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    borderRadius: "8px 8px 0 0",
                    textTransform: "none",
                    bgcolor: mapModal.activeType === "in" ? "#fff" : "transparent",
                    color: mapModal.activeType === "in" ? "#004AAD" : "#64748b",
                    border: mapModal.activeType === "in" ? "1px solid #e2e8f0" : "none",
                    borderBottom: mapModal.activeType === "in" ? "2px solid #004AAD" : "none",
                    boxShadow: mapModal.activeType === "in" ? "0 -2px 4px rgba(0,0,0,0.02)" : "none",
                    "&:hover": { bgcolor: mapModal.activeType === "in" ? "#fff" : "#f1f5f9" },
                  }}
                >
                  IN TIME: {inLoc.time} {inLoc.clockNo !== "—" ? `(Clock ${inLoc.clockNo})` : ""}
                </Button>
                <Button
                  onClick={() => setMapModal((prev) => ({ ...prev, activeType: "out" }))}
                  sx={{
                    flex: 1,
                    py: 1,
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    borderRadius: "8px 8px 0 0",
                    textTransform: "none",
                    bgcolor: mapModal.activeType === "out" ? "#fff" : "transparent",
                    color: mapModal.activeType === "out" ? "#004AAD" : "#64748b",
                    border: mapModal.activeType === "out" ? "1px solid #e2e8f0" : "none",
                    borderBottom: mapModal.activeType === "out" ? "2px solid #004AAD" : "none",
                    boxShadow: mapModal.activeType === "out" ? "0 -2px 4px rgba(0,0,0,0.02)" : "none",
                    "&:hover": { bgcolor: mapModal.activeType === "out" ? "#fff" : "#f1f5f9" },
                  }}
                >
                  OUT TIME: {outLoc.time} {outLoc.clockNo !== "—" ? `(Clock ${outLoc.clockNo})` : ""}
                </Button>
              </Box>

              {/* Map Canvas / Container */}
              <DialogContent sx={{ p: 0, height: "360px", bgcolor: "#f8fafc", position: "relative" }}>
                {hasValidCoords ? (
                  <iframe
                    title="Attendance Location Map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0, display: "block" }}
                    src={mapSrc}
                    allowFullScreen
                  />
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 3,
                      textAlign: "center",
                    }}
                  >
                    <LocationOn sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                    <Typography sx={{ color: "#475569", fontWeight: 600, fontSize: "0.9rem", mb: 0.5 }}>
                      No GPS Coordinates Available
                    </Typography>

                  </Box>
                )}
              </DialogContent>

              {/* Bottom Footer with Full Width Open Google Maps button */}
              {hasValidCoords && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#f8fafc",
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <Button
                    fullWidth
                    onClick={openExternalMap}
                    variant="contained"
                    startIcon={<LocationOn sx={{ fontSize: 18 }} />}
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      textTransform: "none",
                      bgcolor: "#004AAD",
                      color: "#fff",
                      py: 1,
                      borderRadius: "10px",
                      boxShadow: "0 4px 12px rgba(0,74,173,0.25)",
                      "&:hover": { bgcolor: "#00367d" },
                    }}
                  >
                    Open Google Maps
                  </Button>
                </Box>
              )}
            </Box>
          );
        })()}
      </Dialog>
    </Box>
  );
};

// ─── PettyCashTab ─────────────────────────────────────────────────────────
const PettyCashTab = ({ sno }) => {
  const [pettyCashData, setPettyCashData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear().toString());

  const getAuthKey = () => {
    const token = localStorage.getItem("token");
    if (token) return token.replace(/^"|"$/g, "");
    return null;
  };

  const fetchPettyCashData = async () => {
    if (!sno) return;
    const authKey = getAuthKey();
    if (!authKey) {
      setError("Authentication key not found");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${axios.defaults.baseURL}Pettycash/DGESGetPettyCashAdvances?p_year=${selectedYear}&p_sno=${sno}`,
        {
          headers: {
            "auth-key": authKey,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();

      if (result?.StatusCode === 200 && Array.isArray(result.ResultSet)) {
        const sortedData = [...result.ResultSet].sort((a, b) => {
          if (a.MONTH === "TOTAL") return 1;
          if (b.MONTH === "TOTAL") return -1;
          const monthMap = {
            "01": 1, "02": 2, "03": 3, "04": 4, "05": 5, "06": 6,
            "07": 7, "08": 8, "09": 9, "10": 10, "11": 11, "12": 12
          };
          return (monthMap[a.MONTH] || 0) - (monthMap[b.MONTH] || 0);
        });
        setPettyCashData(sortedData);
      } else {
        setPettyCashData([]);
        setError(result?.Message || "Failed to load Petty Cash data");
      }
    } catch (e) {
      console.error("Petty Cash fetch error:", e);
      setError("Network error - Failed to load Petty Cash data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sno && selectedYear) {
      fetchPettyCashData();
    }
  }, [sno, selectedYear]);

  const handleYearChange = (event) => setSelectedYear(event.target.value);

  const getYearOptions = () => {
    const currentYearNum = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) years.push((currentYearNum - i).toString());
    return years;
  };

  const getMonthName = (monthNum) => {
    if (!monthNum || monthNum.trim() === "") return "Opening Balance";
    if (monthNum === "TOTAL") return "TOTAL";
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const idx = parseInt(monthNum) - 1;
    return idx >= 0 && idx < 12 ? months[idx] : monthNum;
  };

  const formatCurrency = (value) => {
    if (!value) return "0.00";
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate totals
  const totals = useMemo(() => {
    let totalCredit = 0;
    let totalDebit = 0;
    let totalNet = 0;

    pettyCashData.forEach((item) => {
      if (item.MONTH !== "TOTAL" && item.MONTH?.trim() !== "") {
        totalCredit += parseFloat(item.CREDIT) || 0;
        totalDebit += parseFloat(item.DEBIT) || 0;
        totalNet += parseFloat(item.NET) || 0;
      }
    });

    return { totalCredit, totalDebit, totalNet };
  }, [pettyCashData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#004AAD" }} size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, height: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Header & Controls */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, color: "#004AAD", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 0.8 }}>
          <AttachMoney sx={{ fontSize: 16 }} />
          Petty Cash Advances
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}>Year:</Typography>
          <Box
            component="select"
            value={selectedYear}
            onChange={handleYearChange}
            sx={{
              px: 1.5,
              py: 0.6,
              fontSize: "0.75rem",
              fontWeight: 600,
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              backgroundColor: "#fff",
              color: "#004AAD",
              cursor: "pointer",
              outline: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.2s ease",
              "&:focus": { borderColor: "#004AAD", boxShadow: "0 0 0 2px rgba(0,74,173,0.1)" },
              "&:hover": { borderColor: "#004AAD" },
            }}
          >
            {getYearOptions().map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Main Table Container */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-track": { bgcolor: "#f1f5f9", borderRadius: 3 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 3, "&:hover": { bgcolor: "#94a3b8" } },
        }}
      >
        {error ? (
          <Box sx={{ textAlign: "center", py: 5, bgcolor: "#fff", borderRadius: "12px", border: "0.5px solid #e2e8f0" }}>
            <AttachMoney sx={{ fontSize: 44, color: "#918e8e", mb: 1 }} />
            <Typography sx={{ color: "#918e8e", fontSize: "0.82rem" }}>{error}</Typography>
          </Box>
        ) : pettyCashData.length === 0 ? (
          <PlaceholderTab icon={<AttachMoney sx={{ fontSize: 44 }} />} message={`No Petty Cash records found for ${selectedYear}`} />
        ) : (
          <Box
            sx={{
              border: "1px solid #cbd5e1",
              borderRadius: "12px",
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "50px 1fr 1.2fr 1.2fr 1.4fr",
                  sm: "60px 1.5fr 1.2fr 1.2fr 1.5fr",
                  md: "70px 2fr 1.2fr 1.2fr 1.5fr"
                },
                px: { xs: 1, sm: 2 },
                py: 1.1,
                bgcolor: "#f1f5f9",
                position: "sticky",
                top: 0,
                zIndex: 3,
                borderBottom: "1px solid #cbd5e1",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "left", whiteSpace: "nowrap" }}>
                #
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "left", whiteSpace: "nowrap" }}>
                Month
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "right", whiteSpace: "nowrap" }}>
                Credit
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "right", whiteSpace: "nowrap" }}>
                Debit
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.58rem", sm: "0.68rem", md: "0.72rem" }, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textAlign: "right", whiteSpace: "nowrap" }}>
                Net
              </Typography>
            </Box>

            {/* Data Rows */}
            <Box sx={{ maxHeight: { xs: "calc(88vh - 270px)", sm: "calc(85vh - 250px)", md: "520px" }, overflowY: "auto" }}>
              {pettyCashData.map((item, index) => {
                const isOpeningBalance = !item.MONTH || item.MONTH.trim() === "";
                const isTotal = item.MONTH === "TOTAL";
                const monthName = getMonthName(item.MONTH);
                const credit = parseFloat(item.CREDIT) || 0;
                const debit = parseFloat(item.DEBIT) || 0;
                const net = parseFloat(item.NET) || 0;

                // Row styling based on type
                let bgColor = "transparent";
                let fontWeight = 400;
                let textColor = "#1e293b";

                if (isOpeningBalance) {
                  bgColor = "#f8faff";
                  fontWeight = 500;
                  textColor = "#004AAD";
                } else if (isTotal) {
                  bgColor = "#e8f0fe";
                  fontWeight = 700;
                  textColor = "#004AAD";
                }

                // Net color
                const netColor = net > 0 ? "#16a34a" : net < 0 ? "#dc2626" : "#64748b";

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "50px 1fr 1.2fr 1.2fr 1.4fr",
                        sm: "60px 1.5fr 1.2fr 1.2fr 1.5fr",
                        md: "70px 2fr 1.2fr 1.2fr 1.5fr"
                      },
                      px: { xs: 1, sm: 2 },
                      py: isTotal ? 1.3 : 0.85,
                      borderTop: index > 0 ? "1px solid #e2e8f0" : "none",
                      bgcolor: bgColor,
                      alignItems: "center",
                      transition: "all 0.2s ease",
                      "&:hover": { bgcolor: isTotal ? bgColor : "#f8faff" },
                    }}
                  >
                    {/* Column 1: Index */}
                    <Typography
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.82rem" },
                        fontWeight: isTotal ? 700 : 600,
                        color: isTotal ? "#004AAD" : "#94a3b8",
                        textAlign: "left",
                      }}
                    >
                      {isOpeningBalance ? "—" : isTotal ? "∑" : index}
                    </Typography>

                    {/* Column 2: Month Name */}
                    <Typography
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        fontWeight: isTotal ? 700 : isOpeningBalance ? 600 : 500,
                        color: textColor,
                        textAlign: "left",
                      }}
                    >
                      {monthName}
                    </Typography>

                    {/* Column 3: Credit */}
                    <Typography
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        fontWeight: isTotal ? 700 : 500,
                        color: textColor,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {credit > 0 ? formatCurrency(credit) : "-"}
                    </Typography>

                    {/* Column 4: Debit */}
                    <Typography
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        fontWeight: isTotal ? 700 : 500,
                        color: textColor,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {debit > 0 ? formatCurrency(debit) : "-"}
                    </Typography>

                    {/* Column 5: Net */}
                    <Typography
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        fontWeight: isTotal ? 700 : 600,
                        color: isTotal ? "#004AAD" : netColor,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {isOpeningBalance ? "-" : formatCurrency(net)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Summary Footer */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "50px 1fr 1.2fr 1.2fr 1.4fr",
                  sm: "60px 1.5fr 1.2fr 1.2fr 1.5fr",
                  md: "70px 2fr 1.2fr 1.2fr 1.5fr"
                },
                px: { xs: 1, sm: 2 },
                py: 1.3,
                bgcolor: "#004AAD",
                borderTop: "2px solid #004AAD",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#fff" }} />
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff" }}>
                Summary
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {formatCurrency(totals.totalCredit)}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {formatCurrency(totals.totalDebit)}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {formatCurrency(totals.totalNet)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};


/* ─── EmployeeDetailsPopup ────────────────────────────────────────────────── */
const EmployeeDetailsPopup = ({ open, onClose, employee, currentYear }) => {
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [noPayData, setNoPayData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear || new Date().getFullYear().toString());
  const [noPayLoading, setNoPayLoading] = useState(false);
  const [noPayError, setNoPayError] = useState(null);

  useEffect(() => {
    if (open && employee && tabValue === 1 && employeeDetails?.barcode_no) {
      fetchNoPayData();
    }
  }, [open, employee, selectedYear, tabValue, employeeDetails?.barcode_no]);

  useEffect(() => {
    if (open && employee) {
      fetchEmployeeData();
      setTabValue(0);
      setImageError(false);
      setSelectedYear(currentYear ? (parseInt(currentYear) - 1).toString() : (new Date().getFullYear() - 1).toString());
    }
  }, [open, employee, currentYear]);

  const fetchEmployeeData = async () => {
    if (!employee?.sno) return;
    setLoading(true);
    try {
      const detailsResponse = await CommonService.GetEmployeeDetails(employee.sno);
      if (detailsResponse?.data?.StatusCode === 200) {
        const details = detailsResponse.data.ResultSet?.[0] || null;
        setEmployeeDetails(details);
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNoPayData = async () => {
    const barcodeNo = employeeDetails?.barcode_no || employee?.cno || employee?.barcode_no;
    if (!barcodeNo) {
      setNoPayError("Barcode number not found for this employee");
      setNoPayData([]);
      return;
    }
    if (!selectedYear) {
      setNoPayError("No year selected");
      return;
    }
    setNoPayLoading(true);
    setNoPayError(null);
    try {
      const noPayResponse = await CommonService.GetEmployeeNoPay(barcodeNo, selectedYear);
      if (noPayResponse?.data?.StatusCode === 200) {
        const resultSet = noPayResponse.data.ResultSet || [];
        setNoPayData(resultSet);
        if (resultSet.length === 0) setNoPayError(`No pay records found for ${selectedYear}`);
      } else {
        setNoPayData([]);
        setNoPayError(noPayResponse?.data?.Message || `No Pay data is not available for ${selectedYear}`);
      }
    } catch (error) {
      console.error("No Pay fetch error:", error);
      setNoPayError(error?.response?.data?.Message || error?.message || "Network error - Failed to load No Pay data");
      setNoPayData([]);
    } finally {
      setNoPayLoading(false);
    }
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setNoPayError(null);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getEmployeeImageUrl = () => {
    if (!employee?.sno) return null;
    return `https://esystems.cdl.lk/backendDGES/BizTrack/Home/DGESGetUserImg?serviceNo=${employee.sno}`;
  };

  const getYearOptions = () => {
    const currentYearNum = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) years.push((currentYearNum - i).toString());
    return years;
  };

  const noPayTotal = noPayData.reduce((sum, item) => sum + (parseFloat(item.Nopay_Count || item.nopay_count) || 0), 0);

  const TABS = [
    { label: "Profile", icon: <Person sx={{ fontSize: 17 }} /> },
    { label: "No Pay", icon: <ReceiptLong sx={{ fontSize: 17 }} /> },
    { label: "Attendance Card", icon: <CalendarToday sx={{ fontSize: 17 }} /> },
    { label: "Attendance", icon: <AccessTime sx={{ fontSize: 17 }} /> },
    // { label: "Petty Cash", icon: <AttachMoney sx={{ fontSize: 17 }} /> },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: "16px", sm: "24px" },
          maxHeight: "92vh",
          width: { xs: "95vw", sm: "88vw", md: "680px", lg: "740px" },
          maxWidth: { xs: "100%", sm: "640px", md: "740px" },
          m: { xs: 1, sm: 2 },
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Box sx={{ position: "relative", bgcolor: "#004AAD", p: { xs: "14px 16px", sm: "20px 24px" } }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 14, top: 14, color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }, width: 32, height: 32 }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.2, sm: 2 } }}>
          <Avatar
            src={!imageError ? getEmployeeImageUrl() : undefined}
            onError={() => setImageError(true)}
            sx={{ width: { xs: 56, sm: 68 }, height: { xs: 56, sm: 68 }, bgcolor: "#d0dff5", color: "#004AAD", fontSize: { xs: "1.2rem", sm: "1.5rem" }, fontWeight: 500, border: "3px solid rgba(255,255,255,0.85)", flexShrink: 0 }}
          >
            {getInitials(employee?.repname)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.9rem", sm: "1.05rem" }, lineHeight: 1.3 }}>
              {employee?.repname || "-"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: { xs: "0.72rem", sm: "0.8rem" }, mb: 0.8 }}>
              {employee?.des || "No Designation"}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.7, flexWrap: "wrap" }}>
              {employee?.sno && (
                <Chip label={employee.sno} size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontSize: "0.68rem", fontWeight: 600, height: 22, borderRadius: "20px" }} />
              )}
              {employeeDetails?.barcode_no && (
                <Chip label={`BC: ${employeeDetails.barcode_no}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontSize: "0.68rem", fontWeight: 600, height: 22, borderRadius: "20px" }} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "#f8fafc",
          minHeight: { xs: 42, sm: 48 },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "0.7rem", sm: "0.78rem", md: "0.82rem" },
            minHeight: { xs: 42, sm: 48 },
            py: 0,
            gap: { xs: "4px", sm: "6px" },
            color: "#64748b",
            minWidth: "auto",
            flex: { sm: 1 },
            px: { xs: 1.2, sm: 2, md: 2.5 },
            transition: "all 0.2s ease",
          },
          "& .Mui-selected": { color: "#004AAD", fontWeight: 700 },
          "& .MuiTabs-indicator": { backgroundColor: "#004AAD", height: 3, borderRadius: "3px 3px 0 0" },
          "& .MuiTabs-scrollButtons": { color: "#004AAD", "&.Mui-disabled": { opacity: 0.2 } },
        }}
      >
        {TABS.map((t, i) => <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />)}
      </Tabs>

      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#004AAD" }} />
          </Box>
        ) : (
          <>
            {tabValue === 0 && (
              <Box sx={{ px: 2, py: 0.5 }}>
                {employeeDetails ? (
                  <>
                    <InfoCard colorKey="blue" icon={<Work />} label="W.Category" value={employeeDetails.work_category} />
                    <InfoCard
                      colorKey="purple"
                      icon={<Phone />}
                      label="Mobile"
                      value={employeeDetails.mobile_no}
                      action={
                        employeeDetails.mobile_no ? (
                          <Box component="a" href={`tel:${employeeDetails.mobile_no}`} sx={{ width: 34, height: 34, borderRadius: "10px", background: "#eaf3de", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18, color: "#3B6D11" } }}>
                            <Phone />
                          </Box>
                        ) : null
                      }
                    />
                    <InfoCard colorKey="teal" icon={<Business />} label="Division" value={employeeDetails.division} />
                    <InfoCard colorKey="amber" icon={<AccountTree />} label="Department" value={employeeDetails.department} />
                    <InfoCard colorKey="blue" icon={<Email />} label="Email" value={employeeDetails.email} />
                    <InfoCard colorKey="teal" icon={<CalendarToday />} label="Permanant Date" value={employeeDetails.permanant_date} />
                    <InfoCard colorKey="amber" icon={<EventNote />} label="Retirement Date" value={employeeDetails.retirement_date} />
                  </>
                ) : (
                  <PlaceholderTab icon={<Person sx={{ fontSize: 44 }} />} message="No profile data available" />
                )}
              </Box>
            )}

            {tabValue === 1 && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "#004AAD", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 0.8 }}>
                    <AttachMoney sx={{ fontSize: 16 }} /> No Pay Summary
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>Year:</Typography>
                    <Box
                      component="select"
                      value={selectedYear}
                      onChange={handleYearChange}
                      sx={{
                        px: 1.5, py: 0.6, fontSize: "0.75rem", fontWeight: 600,
                        border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#fff",
                        color: "#004AAD", cursor: "pointer", outline: "none",
                        "&:focus": { borderColor: "#004AAD", boxShadow: "0 0 0 2px rgba(0,74,173,0.1)" },
                      }}
                    >
                      {getYearOptions().map((year) => <option key={year} value={year}>{year}</option>)}
                    </Box>
                  </Box>
                </Box>

                {noPayLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress sx={{ color: "#004AAD" }} size={32} />
                  </Box>
                ) : noPayError ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <ReceiptLong sx={{ fontSize: 44, color: "#918e8e", mb: 1 }} />
                    <Typography sx={{ color: "#918e8e", fontSize: "0.82rem", mb: 1 }}>{noPayError}</Typography>
                  </Box>
                ) : noPayData.length === 0 ? (
                  <PlaceholderTab icon={<ReceiptLong sx={{ fontSize: 44 }} />} message={`No pay records found for ${selectedYear}`} />
                ) : (
                  <Box sx={{ border: "0.5px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "36px 1fr auto", px: 2, py: 1.2, bgcolor: "#f1f5f9" }}>
                      {[{ label: "#", align: "left" }, { label: "MONTH", align: "left" }, { label: "COUNT", align: "right" }].map(({ label, align }) => (
                        <Typography key={label} sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.07em", textAlign: align }}>{label}</Typography>
                      ))}
                    </Box>
                    {noPayData.map((item, index) => {
                      let monthName = item.Month || "-";
                      try {
                        if (item.Month?.includes("-")) {
                          const [y, m] = item.Month.split("-");
                          monthName = new Date(parseInt(y), parseInt(m) - 1).toLocaleString("default", { month: "long" });
                        } else {
                          const d = new Date(item.Month);
                          if (!isNaN(d)) monthName = d.toLocaleString("default", { month: "long" });
                        }
                      } catch (_) { }
                      const count = parseFloat(item.Nopay_Count || item.nopay_count) || 0;
                      return (
                        <Box key={index} sx={{ display: "grid", gridTemplateColumns: "36px 1fr auto", px: 2, py: 1.4, borderTop: "0.5px solid #e2e8f0", bgcolor: "#fff", alignItems: "center", "&:hover": { bgcolor: "#f8faff" } }}>
                          <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 600 }}>{index + 1}</Typography>
                          <Typography sx={{ fontSize: "0.88rem", color: "#1e293b" }}>{monthName}</Typography>
                          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#1e293b", textAlign: "right" }}>{count.toFixed(2)}</Typography>
                        </Box>
                      );
                    })}
                    <Box sx={{ display: "grid", gridTemplateColumns: "36px 1fr auto", px: 2, py: 1.3, borderTop: "0.5px solid #e2e8f0", bgcolor: "#f8faff", alignItems: "center" }}>
                      <Box />
                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.07em" }}>TOTAL</Typography>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#dc2626", textAlign: "right" }}>{noPayTotal.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {tabValue === 2 && <AttendanceCardTab sno={employee?.sno} />}
            {tabValue === 3 && <AttendanceTab sno={employee?.sno} />}
            {tabValue === 4 && <PettyCashTab sno={employee?.sno} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};


/* ─── STEP 1: Division bar-list (was card-style DivisionStep) ─────────────── */
const DivisionStep = ({ divisions, data, onSelect, scaleMode = "percentage" }) => {
  const chartData = useMemo(() => {
    return divisions
      .map((div) => {
        const emps = data.filter((d) => d.division === div);
        const divName = emps[0]?.divName || "";
        const present = emps.filter(isPresent).length;
        const total = emps.length;
        return {
          division: div,
          divName,
          total,
          present,
          percentage: total > 0 ? Math.round((present / total) * 100) : 0,
        };
      })
      .sort((a, b) => a.division.localeCompare(b.division));
  }, [divisions, data]);

  const maxTotal = Math.max(...chartData.map((r) => r.total), 1);

  if (chartData.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <Search sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
        <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>No divisions match your search</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 1,
        p: 2,
        borderRadius: "16px",
        background: "#fff",
        border: "1.5px solid #e2e8f0",
        boxShadow: "0 2px 12px rgba(0,74,173,0.06)",
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#004AAD", letterSpacing: "0.03em" }}>
          Division Overview
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#94a3b8", mt: "1px" }}>
          {chartData.length} divisions · click any division to view locations
        </Typography>
      </Box>

      {chartData.map((row) => {
        const barW =
          scaleMode === "percentage"
            ? Math.max(row.percentage, row.present > 0 ? 2 : 0)
            : Math.max(Math.round((row.present / maxTotal) * 100), row.present > 0 ? 2 : 0);
        const barColor = rateColor(row.percentage);

        return (
          <Box key={row.division} sx={{ mb: "6px", "&:last-child": { mb: 0 } }} onClick={() => onSelect(row.division)}>
            <Box
              sx={{
                cursor: "pointer",
                borderRadius: "8px",
                p: "8px 10px",
                mx: "-8px",
                transition: "all 0.18s ease",
                "&:hover": { bgcolor: "#f0f5ff", transform: "translateX(4px)" },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0, flex: 1 }}>
                  <AccountTree sx={{ fontSize: 12, color: "#004AAD", flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.division}
                    </Typography>
                    {row.divName && (
                      <Typography sx={{ fontSize: "10px", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.divName}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0, ml: 1 }}>
                  <Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>{row.present}/{row.total}</Typography>
                  <PctBadge pct={row.percentage} />
                  <KeyboardArrowDown sx={{ fontSize: 14, color: "#94a3b8", transform: "rotate(-90deg)" }} />
                </Box>
              </Box>
              <Box sx={{ height: 5, background: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", borderRadius: "10px", width: `${barW}%`, background: barColor, transition: "width 0.45s ease" }} />
              </Box>
            </Box>
          </Box>
        );
      })}

      <RateLegend />
    </Box>
  );
};

/* ─── STEP 2 (mobile): Location bar-list with inline expand (was card MobileLocationPanel) ── */
const MobileLocationChart = ({ data, division, onEmployeeClick, scaleMode = "percentage" }) => {
  const [expandedLoc, setExpandedLoc] = useState(null);
  const rowRefs = useRef({});

  const chartData = useMemo(() => {
    const filtered = data.filter((d) => d.division === division);
    const locationMap = new Map();
    filtered.forEach((item) => {
      const loc = item.loc?.trim() || "Unknown";
      if (!locationMap.has(loc)) {
        locationMap.set(loc, { location: loc, locName: item.locName || "", employees: [], total: 0, present: 0 });
      }
      const entry = locationMap.get(loc);
      entry.employees.push(item);
      entry.total += 1;
      if (isPresent(item)) entry.present += 1;
    });
    return Array.from(locationMap.values())
      .map((item) => ({ ...item, percentage: item.total > 0 ? Math.round((item.present / item.total) * 100) : 0 }))
      .sort((a, b) => a.location.localeCompare(b.location));
  }, [data, division]);

  useEffect(() => { setExpandedLoc(null); }, [division]);

  useEffect(() => {
    if (expandedLoc && rowRefs.current[expandedLoc]) {
      const timer = setTimeout(() => {
        rowRefs.current[expandedLoc]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [expandedLoc]);

  const maxTotal = Math.max(...chartData.map((r) => r.total), 1);

  if (chartData.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Search sx={{ fontSize: 44, color: "#cbd5e1", mb: 1 }} />
        <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>No locations match your search</Typography>
      </Box>
    );
  }

  const handleBarClick = (locName) => setExpandedLoc((prev) => (prev === locName ? null : locName));

  return (
    <Box
      sx={{
        mb: 1,
        p: 2,
        borderRadius: "16px",
        background: "#fff",
        border: "1.5px solid #e2e8f0",
        boxShadow: "0 2px 12px rgba(0,74,173,0.06)",
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#004AAD", letterSpacing: "0.03em" }}>
          Location Overview - {division}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#94a3b8", mt: "1px" }}>
          {chartData.length} locations · click bar to expand
        </Typography>
      </Box>

      <Box sx={{ mx: "-8px", px: "8px" }}>
        {chartData.map((row) => {
          const barW =
            scaleMode === "percentage"
              ? Math.max(row.percentage, row.present > 0 ? 2 : 0)
              : Math.max(Math.round((row.present / maxTotal) * 100), row.present > 0 ? 2 : 0);
          const isOpen = expandedLoc === row.location;
          const barColor = rateColor(row.percentage);

          return (
            <Box
              key={row.location}
              ref={(el) => { rowRefs.current[row.location] = el; }}
              sx={{ mb: "6px", "&:last-child": { mb: 0 }, transition: "all 0.22s ease", boxShadow: isOpen ? "0 4px 12px rgba(0,74,173,0.08)" : "none", borderRadius: "8px" }}
            >
              <Box
                onClick={() => handleBarClick(row.location)}
                sx={{
                  cursor: "pointer",
                  borderRadius: isOpen ? "8px 8px 0 0" : "8px",
                  p: "6px 8px",
                  mx: "-8px",
                  border: isOpen ? `1.5px solid ${barColor}` : "1.5px solid transparent",
                  borderBottom: isOpen ? "none" : undefined,
                  bgcolor: isOpen ? `${barColor}15` : "transparent",
                  transition: "background 0.15s ease, border-color 0.15s ease",
                  "&:hover": { bgcolor: isOpen ? `${barColor}15` : "#f0f5ff" },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0, flex: 1 }}>
                    <LocationOn sx={{ fontSize: 12, color: isOpen ? barColor : "#004AAD", flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.location}
                      </Typography>
                      {row.locName && (
                        <Typography sx={{ fontSize: "10px", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.locName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0, ml: 1 }}>
                    <Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>{row.present}/{row.total}</Typography>
                    <PctBadge pct={row.percentage} />
                    <KeyboardArrowDown sx={{ fontSize: 14, color: "#94a3b8", transition: "transform 0.22s ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </Box>
                </Box>
                <Box sx={{ height: 5, background: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", borderRadius: "10px", width: `${barW}%`, background: barColor, transition: "width 0.45s ease" }} />
                </Box>
              </Box>

              <Collapse in={isOpen} timeout={220} unmountOnExit>
                <Box sx={{ mx: "-8px", border: `1.5px solid ${barColor}`, borderTop: "none", borderRadius: "0 0 8px 8px", overflow: "hidden", bgcolor: "#fafbff" }}>
                  <Box sx={{ display: "flex", bgcolor: "#fff", borderBottom: "1px solid #f1f5f9" }}>
                    {[
                      { label: "Strength", value: row.total, color: "#004AAD" },
                      { label: "Present", value: row.present, color: "#16a34a" },
                      { label: "Absent", value: row.total - row.present, color: "#dc2626" },
                    ].map((s) => (
                      <Box key={s.label} sx={{ flex: 1, textAlign: "center", py: 1, borderRight: "1px solid #f1f5f9", "&:last-child": { borderRight: "none" } }}>
                        <Typography sx={{ fontSize: "0.6rem", color: "#64748b" }}>{s.label}</Typography>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: s.color }}>{s.value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.6fr 1.4fr 0.7fr 0.5fr", gap: 0.5, px: 1.5, py: 0.7, bgcolor: "#e8f0fe" }}>
                    {["Svc No", "Name", "Designation", "IN", "OUT"].map((h) => (
                      <Typography key={h} sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#004AAD" }}>{h}</Typography>
                    ))}
                  </Box>

                  <Box sx={{ mx: "-4px", px: "4px" }}>
                    {row.employees.map((emp, idx) => {
                      const p = isPresent(emp);
                      return (
                        <Box
                          key={emp.sno || idx}
                          onClick={() => onEmployeeClick(emp)}
                          sx={{
                            display: "grid", gridTemplateColumns: "1fr 1.6fr 1.4fr 0.7fr 0.5fr", gap: 0.5,
                            px: 1.5, py: 0.85,
                            bgcolor: idx % 2 === 0 ? "#fff" : "#f8faff",
                            borderTop: "0.5px solid #f1f5f9",
                            alignItems: "center", cursor: "pointer", transition: "all 0.18s ease",
                            "&:hover": { bgcolor: "#e8f0fe", transform: "translateX(3px)" },
                          }}
                        >
                          <Typography sx={{ fontSize: "0.65rem", color: "#475569" }}>{emp.sno || "-"}</Typography>
                          <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#1e293b", lineHeight: 1.2, wordBreak: "break-word" }}>{emp.repname || "-"}</Typography>
                          <Typography sx={{ fontSize: "0.6rem", color: "#64748b", lineHeight: 1.2, wordBreak: "break-word" }}>{emp.des || "-"}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: p ? 700 : 400, color: p ? "#16a34a" : "#dc2626" }}>{p ? emp.inn : "NR"}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: p ? 700 : 400, color: p ? "#16a34a" : "#dc2626" }}>{p ? emp.pout : "NR"}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <RateLegend />
    </Box>
  );
};

/* ─── STEP 2 (desktop): Location table with inline bar (was DesktopLocationTable) ─────────── */
const DesktopLocationTable = ({ locationGroups, expandedRow, onExpand, onEmployeeClick, scaleMode = "percentage" }) => {
  const maxTotal = useMemo(() => {
    const totals = Object.values(locationGroups).map((emps) => emps.length);
    return Math.max(...totals, 1);
  }, [locationGroups]);

  return (
    <TableContainer>
      <Table>
        <TableHead sx={{ bgcolor: "#004AAD" }}>
          <TableRow>
            <TableCell sx={{ color: "white", fontWeight: "bold", width: "35%" }}>Location</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Strength</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Present</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Attendance</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(locationGroups).map(([location, employees]) => {
            const strength = employees.length;
            const present = employees.filter(isPresent).length;
            const absent = strength - present;
            const rate = strength > 0 ? Math.round((present / strength) * 100) : 0;
            const barColor = rateColor(rate);
            const expanded = expandedRow === location;
            const locName = employees[0]?.locName || "";
            const barW =
              scaleMode === "percentage"
                ? Math.max(rate, present > 0 ? 2 : 0)
                : Math.max(Math.round((present / maxTotal) * 100), present > 0 ? 2 : 0);

            return (
              <React.Fragment key={location}>
                <TableRow hover sx={{ "&:hover": { bgcolor: "rgba(0,74,173,0.04)" } }}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocationOn sx={{ color: "#004AAD", fontSize: 18 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b" }}>{location}</Typography>
                        {locName && <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>{locName}</Typography>}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center"><Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>{strength}</Typography></TableCell>
                  <TableCell align="center"><Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>{present}</Typography></TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <PctBadge pct={rate} />
                      <Box sx={{ width: 70, height: 5, background: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                        <Box sx={{ height: "100%", borderRadius: "10px", width: `${barW}%`, background: barColor, transition: "width 0.45s ease" }} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => onExpand(location)} sx={{ color: "#004AAD" }}>
                      {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 0 }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <Box sx={{ m: 2, bgcolor: "#f8faff", borderRadius: "12px", p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold", color: "#004AAD" }}>
                          Employee List - {location}{locName ? ` · ${locName}` : ""}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "#e8f0fe" }}>
                              {["Service No", "Name", "Designation", "Clock No", "IN Time", "Prev. OUT", "Status"].map((h) => (
                                <TableCell key={h}><b>{h}</b></TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {employees.map((emp, idx) => {
                              const p = isPresent(emp);
                              return (
                                <TableRow key={emp.sno || idx} hover sx={{ cursor: "pointer" }} onClick={() => onEmployeeClick(emp)}>
                                  <TableCell>{emp.sno || "-"}</TableCell>
                                  <TableCell>{emp.repname || "-"}</TableCell>
                                  <TableCell>{emp.des || "-"}</TableCell>
                                  <TableCell>{emp.cno || "-"}</TableCell>
                                  <TableCell>
                                    <Chip label={p ? emp.inn : "NR"} size="small" sx={{ bgcolor: p ? "#dcfce7" : "#fee2e2", color: p ? "#16a34a" : "#dc2626", fontSize: "0.7rem" }} />
                                  </TableCell>
                                  <TableCell>{emp.pout || "-"}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={p ? "Present" : "Absent"}
                                      size="small"
                                      icon={p ? <CheckCircle sx={{ fontSize: "12px !important" }} /> : <Cancel sx={{ fontSize: "12px !important" }} />}
                                      sx={{ bgcolor: p ? "#dcfce7" : "#fee2e2", color: p ? "#16a34a" : "#dc2626", fontSize: "0.7rem" }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
          {Object.keys(locationGroups).length === 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: "center", py: 5 }}>
                <Search sx={{ fontSize: 40, color: "#cbd5e1", display: "block", mx: "auto", mb: 1 }} />
                <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>No locations match your search</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/* ─── Breadcrumb ──────────────────────────────────────────────────────────── */
const Breadcrumb = ({ division, onBack }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
    <IconButton size="small" onClick={onBack} sx={{ color: "#004AAD", bgcolor: "#e8f0fe", borderRadius: "8px", "&:hover": { bgcolor: "#d0e2ff" } }}>
      <ArrowBack fontSize="small" />
    </IconButton>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
      <Typography onClick={onBack} sx={{ fontSize: "0.8rem", color: "#004AAD", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
        All Divisions
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>/</Typography>
      <Chip label={division} size="small" sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, bgcolor: "#004AAD", color: "#fff" }} />
    </Box>
  </Box>
);

/* ─── SearchBar ───────────────────────────────────────────────────────────── */
const SearchBar = ({ value, onChange, placeholder }) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", gap: 1, mb: 2, px: 1.5, py: 0.85,
      border: "1.5px solid #e2e8f0", borderRadius: "12px", bgcolor: "#fff",
      transition: "border-color 0.18s ease, box-shadow 0.18s ease",
      "&:focus-within": { borderColor: "#004AAD", boxShadow: "0 0 0 3px rgba(0,74,173,0.08)" },
    }}
  >
    <Search sx={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
    <Box
      component="input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "Search…"}
      sx={{ flex: 1, border: "none", outline: "none", fontSize: "0.85rem", color: "#1e293b", bgcolor: "transparent", fontFamily: "inherit", "&::placeholder": { color: "#94a3b8" } }}
    />
    {value && (
      <IconButton size="small" onClick={() => onChange("")} sx={{ color: "#94a3b8", p: 0.3, "&:hover": { color: "#004AAD", bgcolor: "#e8f0fe" }, borderRadius: "6px" }}>
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Box>
);

/* ─── DGESatt (inner) ─────────────────────────────────────────────────────── */
const DGESatt = ({ data = [], loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentYear = new Date().getFullYear().toString();

  const [selectedDivision, setSelectedDivision] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [employeePopupOpen, setEmployeePopupOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [scaleMode, setScaleMode] = useState("percentage");

  const scrollRef = useRef(null);

  const handleDivisionSelect = useCallback((div) => {
    setSelectedDivision(div);
    setExpandedRow(null);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedDivision(null);
    setExpandedRow(null);
    setSearchTerm("");
  }, []);

  const handleToggle = useCallback((loc) => setExpandedRow((prev) => (prev === loc ? null : loc)), []);
  const handleEmployeeClick = useCallback((emp) => { setSelectedEmployee(emp); setEmployeePopupOpen(true); }, []);

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: "20px" }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <Typography>Loading DGES location attendance...</Typography>
        </Box>
      </Paper>
    );
  }

  const term = searchTerm.toLowerCase().trim();
  const filteredData = term
    ? data.filter(
      (d) =>
        d.repname?.toLowerCase().includes(term) ||
        d.sno?.toLowerCase().includes(term) ||
        d.des?.toLowerCase().includes(term) ||
        d.division?.toLowerCase().includes(term) ||
        d.divName?.toLowerCase().includes(term) ||
        d.loc?.toLowerCase().includes(term) ||
        d.locName?.toLowerCase().includes(term)
    )
    : data;

  const divisions = [...new Set(filteredData.map((d) => d.division).filter(Boolean))].sort();

  const locationGroups = {};
  if (selectedDivision) {
    filteredData.filter((d) => d.division === selectedDivision).forEach((item) => {
      const loc = item.loc?.trim() || "Unknown";
      if (!locationGroups[loc]) locationGroups[loc] = [];
      locationGroups[loc].push(item);
    });
  }

  const totalLocations = Object.keys(locationGroups).length;
  const divEmployees = selectedDivision ? filteredData.filter((d) => d.division === selectedDivision) : [];
  const totalPresent = divEmployees.filter(isPresent).length;

  const subtitle = selectedDivision
    ? `${totalLocations} locations · ${divEmployees.length} employees · ${totalPresent} present`
    : `${divisions.length} divisions · ${filteredData.length} employees total`;

  const searchPlaceholder = selectedDivision
    ? "Search by name, service no, designation, location…"
    : "Search by name, service no, designation, division…";

  return (
    <>
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: "24px", border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,74,173,0.05)" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 2,
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: "#16388a" }}>
            DGES Attendance Based on Division
          </Typography>

          <Box
            sx={{
              display: "inline-flex",
              bgcolor: "#f1f5f9",
              p: "3px",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
            }}
          >
            <Button
              size="small"
              onClick={() => setScaleMode("percentage")}
              sx={{
                px: 1.5,
                py: "2px",
                fontSize: "0.72rem",
                fontWeight: scaleMode === "percentage" ? 700 : 500,
                color: scaleMode === "percentage" ? "#004AAD" : "#64748b",
                bgcolor: scaleMode === "percentage" ? "#fff" : "transparent",
                boxShadow: scaleMode === "percentage" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                borderRadius: "7px",
                textTransform: "none",
                minWidth: "auto",
                transition: "all 0.18s ease",
                "&:hover": {
                  bgcolor: scaleMode === "percentage" ? "#fff" : "#e2e8f0",
                },
              }}
            >
              Default View 
            </Button>
            <Button
              size="small"
              onClick={() => setScaleMode("headcount")}
              sx={{
                px: 1.5,
                py: "2px",
                fontSize: "0.72rem",
                fontWeight: scaleMode === "headcount" ? 700 : 500,
                color: scaleMode === "headcount" ? "#004AAD" : "#64748b",
                bgcolor: scaleMode === "headcount" ? "#fff" : "transparent",
                boxShadow: scaleMode === "headcount" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                borderRadius: "7px",
                textTransform: "none",
                minWidth: "auto",
                transition: "all 0.18s ease",
                "&:hover": {
                  bgcolor: scaleMode === "headcount" ? "#fff" : "#e2e8f0",
                },
              }}
            >
              Company Standard View
            </Button>
          </Box>
        </Box>

        <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder={searchPlaceholder} />

        {selectedDivision && <Breadcrumb division={selectedDivision} onBack={handleBack} />}

        {!selectedDivision ? (
          <DivisionStep divisions={divisions} data={filteredData} onSelect={handleDivisionSelect} scaleMode={scaleMode} />
        ) : isMobile ? (
          <Box
            ref={scrollRef}
            sx={{
              maxHeight: "calc(100vh - 260px)",
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 },
            }}
          >
            <MobileLocationChart data={filteredData} division={selectedDivision} onEmployeeClick={handleEmployeeClick} scaleMode={scaleMode} />
            <Box sx={{ height: 80 }} />
          </Box>
        ) : (
          <DesktopLocationTable
            locationGroups={locationGroups}
            expandedRow={expandedRow}
            onExpand={handleToggle}
            onEmployeeClick={handleEmployeeClick}
            scaleMode={scaleMode}
          />
        )}
      </Paper>

      <EmployeeDetailsPopup
        open={employeePopupOpen}
        onClose={() => { setEmployeePopupOpen(false); setTimeout(() => setSelectedEmployee(null), 300); }}
        employee={selectedEmployee}
        currentYear={currentYear}
      />
    </>
  );
};

/* ─── Data Wrapper (exported) ─────────────────────────────────────────────── */
export const CDLLocBaseAttendance = ({ selectedDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const dateParam = selectedDate || new Date().toISOString().split("T")[0];
        const response = await CommonService.GetCdllocbaseAttendance(dateParam);
        const resultSet = response?.data?.ResultSet || [];
        const normalized = resultSet.map(normalizeRow);
        if (active) setData(normalized);
      } catch (err) {
        if (active) setError("Failed to load DGES location attendance.");
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => { active = false; };
  }, [selectedDate]);

  if (error) {
    return (
      <Paper sx={{ p: 3, borderRadius: "20px" }}>
        <Typography sx={{ color: "#dc2626", fontWeight: 600 }}>{error}</Typography>
      </Paper>
    );
  }

  return <DGESatt data={data} loading={loading} />;
};